/**
 * El organigrama comercial del CRM: quién coordina y quién le reporta.
 *
 * Hoy lo consume **solo la reasignación en lote** de la antesala, para saber a
 * quién se le puede pasar una interacción. El panel «Gestión de mi equipo»
 * deliberadamente NO lo usa todavía: espera el contrato de la cartera ya
 * agregada (ver la cabecera de `teamPanelMock.js`).
 *
 * Todo sale de **un solo endpoint**: `fxCrm/FxProfile/`, que es el que ya
 * alimenta «Administrar usuarios». Cada perfil trae:
 *   · `groups` — de ahí se deduce el cargo.
 *   · `coordinador_asignado` — su coordinador.
 *
 * ⚠️ **`coordinador_asignado` es hoy un campo DERIVADO.** Desde la rama de
 * grupos (`feature/crm_groups_admin_itx`), la jerarquía se administra en
 * `Configuraciones → Grupos` (`fxCrm/CrmGroup/`) y «Administrar usuarios» ya no
 * la edita, solo la muestra. Leerlo del perfil sigue siendo correcto —el back lo
 * mantiene al día— y aquí basta, porque para reasignar solo hacen falta personas
 * y no el nombre del grupo.
 *
 * Si algún día hace falta el nombre del equipo, o si el perfil dejara de traer
 * ese campo, la fuente a la que hay que moverse es `fxCrm/CrmGroup/?limit=200`
 * → `{ id, name, coordinator_detail, members_detail[], members_count,
 * people_count }`. Pendiente de confirmar que un COORDINADOR CRM pueda leer ese
 * endpoint: la pantalla que lo usa es solo de administrador.
 *
 * Se descartó `FxProfile/coordinadores-disponibles/` como fuente de los
 * coordinadores aunque exista: «disponibles» es un criterio del formulario de
 * administración, no necesariamente «todos los coordinadores», y mezclar dos
 * endpoints arriesga que los ids de un lado no casen con los
 * `coordinador_asignado` del otro. Derivándolo de la misma lista, casa siempre.
 */
import { apiClient } from '@/api/softphone_api';

export const ADMIN_GROUP = 'ADMINISTRADOR CRM';
export const COORDINATOR_GROUP = 'COORDINADOR CRM';

/** Es la plantilla del CRM, no un listado de gestión: cabe de sobra. */
const PROFILES_LIMIT = 200;

/**
 * El organigrama no cambia mientras dura la sesión, y lo piden dos vistas.
 * `null` = no se ha pedido; una promesa en curso se reutiliza para que dos
 * llamadas simultáneas no disparen dos peticiones.
 */
let cache = null;

/**
 * `nombre_completo` lo trae `FxProfile`; `full_name`, los `*_detail` de
 * `CrmGroup`. Se aceptan los dos para que sirva con cualquiera de las dos
 * fuentes de la jerarquía.
 */
export const nombreDe = (perfil) =>
  perfil?.nombre_completo ||
  perfil?.full_name ||
  `${perfil?.first_name || ''} ${perfil?.last_name || ''}`.trim() ||
  perfil?.username ||
  `Usuario ${perfil?.id}`;

export const cargoDe = (perfil) => {
  const grupos = perfil?.groups || [];
  if (grupos.includes(COORDINATOR_GROUP)) return 'Coordinador comercial';
  if (grupos.includes(ADMIN_GROUP)) return 'Administrador CRM';
  return 'Ejecutivo comercial';
};

const aPersona = (perfil) => ({
  id: perfil.id,
  name: nombreDe(perfil),
  role: cargoDe(perfil),
});

const esCoordinador = (perfil) => (perfil?.groups || []).includes(COORDINATOR_GROUP);

/**
 * El organigrama completo.
 *
 * ⚠️ `coordinador_asignado` solo se llena para quien tenga COMERCIAL CRM (es la
 * condición del formulario de administración), así que un coordinador sin gente
 * asignada sale con `members: []`. Eso es un dato, no un error: significa que a
 * nadie se le ha puesto ese coordinador todavía.
 *
 * @returns {Promise<{coordinators: Array, people: Array, byId: Map}>}
 */
export function fetchOrgChart() {
  if (cache) return cache;

  cache = apiClient
    .get(`api/fxCrm/FxProfile/?limit=${PROFILES_LIMIT}`)
    .then(({ data }) => {
      const perfiles = data?.results || data?.data?.results || [];

      const coordinators = perfiles.filter(esCoordinador).map((perfil) => ({
        ...aPersona(perfil),
        members: perfiles
          .filter(
            (otro) =>
              String(otro?.coordinador_asignado?.id) === String(perfil.id) &&
              otro.id !== perfil.id
          )
          .map(aPersona)
          .sort((a, b) => a.name.localeCompare(b.name)),
      }));

      const people = perfiles.map(aPersona);
      return {
        coordinators,
        people,
        byId: new Map(people.map((persona) => [String(persona.id), persona])),
      };
    })
    .catch((error) => {
      // No se deja la promesa fallida en caché: al reintentar tiene que volver
      // a pedirla, no repetir el error para siempre.
      cache = null;
      throw error;
    });

  return cache;
}

/** Fuerza a releer el organigrama (p. ej. tras tocar «Administrar usuarios»). */
export function invalidateOrgChart() {
  cache = null;
}

/** El coordinador al que pertenece alguien; él mismo si lo es; `null` si no. */
export function coordinadorDe(org, personId) {
  if (!org) return null;
  const propio = org.coordinators.find((c) => String(c.id) === String(personId));
  if (propio) return propio;
  return (
    org.coordinators.find((c) =>
      c.members.some((m) => String(m.id) === String(personId))
    ) || null
  );
}

/**
 * El equipo de un coordinador, **él incluido**: un coordinador también gestiona
 * cartera propia, así que aparece como una persona más dentro de su equipo.
 */
export function equipoDe(coordinator) {
  if (!coordinator) return [];
  return [{ id: coordinator.id, name: coordinator.name, role: coordinator.role }, ...coordinator.members];
}
